import React from "react";
import { Button, Card, Empty, Space, Tabs, Tag, Typography } from "antd";
import {
  AndroidOutlined,
  AppleOutlined,
  AppstoreOutlined,
  CustomerServiceOutlined,
  DesktopOutlined,
  DownloadOutlined,
  GlobalOutlined,
  ShoppingCartOutlined,
  WindowsOutlined,
} from "@ant-design/icons";
import { Navigate, Routes, Route, useNavigate, useLocation } from "react-router-dom";

import FavoriteTracks from "./FavoriteTracks";
import ByAlbums from "./ByAlbums";
import ByArtists from "./ByArtists";
import ByGenres from "./ByGenres";
import AlbumTracksPage from "./AlbumTracksPage";
import ArtistPage from "./ArtistPage";
import DownloadQueuePage from "./DownloadQueuePage";
import FloatingCartButton from "./FloatingCartButton";
import "./DownloadsPage.css";

const { TabPane } = Tabs;
const { Text, Title } = Typography;

const softwareItems = [
  {
    key: "ruznama-pc",
    title: "Ruznama PC",
    platform: "Windows",
    version: "EXE",
    status: "Доступно",
    description: "Установщик Ruznama Prayer Lock для Windows. Если не знаете архитектуру, выбирайте x64.",
    icon: <WindowsOutlined />,
    iconClassName: "software-card__icon--windows",
    appIconUrl: "/downloads/apps/ruznama-pc/icon.png",
    actions: [
      {
        label: "Скачать x64",
        url: "/downloads/apps/ruznama-pc/RuznamaPrayerLockSetup_x64.exe",
      },
      {
        label: "ARM64",
        url: "/downloads/apps/ruznama-pc/RuznamaPrayerLockSetup_arm64.exe",
      },
    ],
  },
  {
    key: "finansi-android",
    title: "Финансы - доходы и расходы",
    platform: "Android и Web",
    version: "APK / Web",
    status: "Доступно",
    description: "Простой способ управлять личными финансами: быстро записывайте расходы и доходы, отслеживайте бюджеты и анализируйте привычки.",
    icon: <AndroidOutlined />,
    iconClassName: "software-card__icon--android",
    appIconUrl: "/downloads/apps/finansi-android/icon.png",
    actions: [
      {
        label: "Открыть веб-версию",
        url: "https://finansi.simolapps.ru/",
        icon: <GlobalOutlined />,
      },
      {
        label: "Скачать APK",
        url: "/downloads/apps/finansi-android/finansi.apk",
        download: "finansi.apk",
      },
    ],
  },
  {
    key: "android-app",
    title: "SimolApps для Android",
    platform: "Android",
    version: "APK",
    status: "Готовится",
    description: "APK-файлы приложений SimolApps для установки на Android.",
    icon: <AndroidOutlined />,
    iconClassName: "software-card__icon--android",
    url: "",
  },
  {
    key: "ios-app",
    title: "SimolApps для iPhone",
    platform: "iOS",
    version: "IPA / App Store",
    status: "Готовится",
    description: "Ссылки на iOS-приложения и инструкции по установке.",
    icon: <AppleOutlined />,
    iconClassName: "software-card__icon--apple",
    url: "",
  },
  {
    key: "windows-app",
    title: "SimolApps для Windows",
    platform: "Windows",
    version: "EXE / MSI",
    status: "Готовится",
    description: "Установщики программ для компьютеров и ноутбуков Windows.",
    icon: <WindowsOutlined />,
    iconClassName: "software-card__icon--windows",
    url: "",
  },
  {
    key: "desktop-app",
    title: "SimolApps для macOS и Linux",
    platform: "Desktop",
    version: "DMG / ZIP",
    status: "Готовится",
    description: "Настольные версии и архивы программ для установки вручную.",
    icon: <DesktopOutlined />,
    iconClassName: "software-card__icon--desktop",
    url: "",
  },
  {
    key: "web-tools",
    title: "Веб-инструменты SimolApps",
    platform: "Web",
    version: "Ссылка",
    status: "Готовится",
    description: "Закрытые веб-приложения и сервисы для пользователей с подпиской.",
    icon: <GlobalOutlined />,
    iconClassName: "software-card__icon--web",
    url: "",
  },
];

const sectionKeyFromPath = (pathname) =>
  pathname.startsWith("/downloads/audio") ? "audio" : "software";

const audioTabKeyFromPath = (pathname) => {
  if (pathname.startsWith("/downloads/audio/cart")) return "cart";
  if (pathname.startsWith("/downloads/audio/album")) return "albums";
  if (pathname.startsWith("/downloads/audio/albums")) return "albums";
  if (pathname.startsWith("/downloads/audio/artist")) return "artists";
  if (pathname.startsWith("/downloads/audio/artists")) return "artists";
  if (pathname.startsWith("/downloads/audio/genres")) return "genres";
  return "fav";
};

function SoftwareDownloads() {
  return (
    <section className="downloads-section">
      <div className="downloads-section__header">
        <div>
          <Title level={3} className="downloads-section__title">
            ПО
          </Title>
          <Text type="secondary">Приложения, установщики и APK-файлы SimolApps.</Text>
        </div>
      </div>

      {softwareItems.length > 0 ? (
        <div className="software-grid">
          {softwareItems.map((item) => (
            <Card key={item.key} className="software-card" bordered={false}>
              <div className={`software-card__icon ${item.iconClassName || ""}`}>
                {item.icon}
              </div>
              <div className="software-card__body">
                <Space size={8} wrap className="software-card__meta">
                  <Tag color="green">{item.platform}</Tag>
                  <Tag>{item.version}</Tag>
                  <Tag color="blue">{item.status}</Tag>
                </Space>
                <Title level={4} className="software-card__title">
                  {item.title}
                </Title>
                {item.appIconUrl && (
                  <img
                    src={item.appIconUrl}
                    alt=""
                    className="software-card__app-icon"
                  />
                )}
                <Text type="secondary">{item.description}</Text>
                <Space size={8} wrap className="software-card__actions">
                  {item.actions?.length ? (
                    item.actions.map((action, index) => (
                      <Button
                        key={action.url}
                        type={index === 0 ? "primary" : "default"}
                        icon={action.icon || <DownloadOutlined />}
                        href={action.url}
                        download={action.download}
                        target={action.download ? undefined : "_blank"}
                        rel={action.download ? undefined : "noreferrer"}
                      >
                        {action.label}
                      </Button>
                    ))
                  ) : (
                    <Button
                      type="primary"
                      icon={<DownloadOutlined />}
                      href={item.url || undefined}
                      disabled={!item.url}
                    >
                      Скачать
                    </Button>
                  )}
                </Space>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <Empty description="Файлы ПО пока не добавлены" />
      )}
    </section>
  );
}

function AudioDownloads() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <section className="downloads-section">
      <div className="downloads-section__header">
        <div>
          <Title level={3} className="downloads-section__title">
            Аудио
          </Title>
          <Text type="secondary">Треки, альбомы, артисты, жанры и очередь скачивания.</Text>
        </div>
      </div>

      <Tabs
        activeKey={audioTabKeyFromPath(pathname)}
        onChange={(key) => {
          switch (key) {
            case "fav":
              return navigate("/downloads/audio");
            case "albums":
              return navigate("/downloads/audio/albums");
            case "artists":
              return navigate("/downloads/audio/artists");
            case "genres":
              return navigate("/downloads/audio/genres");
            case "cart":
              return navigate("/downloads/audio/cart");
            default:
              return undefined;
          }
        }}
        tabPosition="top"
        className="audio-tabs"
      >
        <TabPane tab="Понравившиеся" key="fav" />
        <TabPane tab="По альбомам" key="albums" />
        <TabPane tab="По артистам" key="artists" />
        <TabPane tab="По жанрам" key="genres" />
        <TabPane tab={<span><ShoppingCartOutlined /> Корзина</span>} key="cart" />
      </Tabs>

      <Routes>
        <Route index element={<FavoriteTracks />} />
        <Route path="albums" element={<ByAlbums />} />
        <Route path="artists" element={<ByArtists />} />
        <Route path="genres" element={<ByGenres />} />
        <Route path="cart" element={<DownloadQueuePage />} />
        <Route path="album/:id" element={<AlbumTracksPage />} />
        <Route path="artist/:artistId" element={<ArtistPage />} />
      </Routes>

      <FloatingCartButton />
    </section>
  );
}

function DownloadsPage() {
  const navigate = useNavigate();
  const { pathname } = useLocation();

  return (
    <div className="container downloads-page">
      <div className="downloads-page__header">
        <Title level={2} className="downloads-page__title">
          Загрузки
        </Title>
        <Text type="secondary">Файлы SimolApps в одном месте.</Text>
      </div>

      <Tabs
        activeKey={sectionKeyFromPath(pathname)}
        onChange={(key) => {
          switch (key) {
            case "software":
              return navigate("/downloads");
            case "audio":
              return navigate("/downloads/audio");
            default:
              return undefined;
          }
        }}
        tabPosition="top"
        className="downloads-main-tabs"
      >
        <TabPane tab={<span><AppstoreOutlined /> ПО</span>} key="software" />
        <TabPane tab={<span><CustomerServiceOutlined /> Аудио</span>} key="audio" />
      </Tabs>

      <Routes>
        <Route index element={<SoftwareDownloads />} />
        <Route path="audio/*" element={<AudioDownloads />} />

        <Route path="albums" element={<Navigate to="/downloads/audio/albums" replace />} />
        <Route path="artists" element={<Navigate to="/downloads/audio/artists" replace />} />
        <Route path="genres" element={<Navigate to="/downloads/audio/genres" replace />} />
        <Route path="cart" element={<Navigate to="/downloads/audio/cart" replace />} />
        <Route path="album/:id" element={<Navigate to={pathname.replace("/downloads/album", "/downloads/audio/album")} replace />} />
        <Route path="artist/:artistId" element={<Navigate to={pathname.replace("/downloads/artist", "/downloads/audio/artist")} replace />} />
      </Routes>
    </div>
  );
}

export default DownloadsPage;
